<template>
  <div class="c-context-select__selection">
    <base-loading :is-active="isLoading" :is-overlay="true" :border-radius="4" />

    <div :class="['c-context-select__selection-col', { 'c-context-select__selection-col--is-active' : activeOption === option.key }]" :key="option.key" v-for="(option, index) in options">
      <div class="c-context-select__selection-col-header">
        <div v-if="index > 0" class="c-context-select__selection-col-header-previous" @click="onPreviousOption(index)">
          <font-awesome-icon :icon="['fas', 'arrow-left']"/>
        </div>
        <div class="c-context-select__selection-col-header-name">
          {{ option.name }}
        </div>
      </div>
      <div class="c-context-select__selection-col-body">
        <content-loader :is-loading="option.isLoading" layout="context-layout">
          <template v-slot:content>
            <ul class="c-context-select__selection-list" key="selector">
              <li class="c-context-select__selection-list-item" :key="policy.id" v-for="policy in optionPolicies(option, index)">
                <label class="c-context-select__selection-list-label" :id="`${option.key}-${policy.id}`">
                  <input type="radio" @change="onSelectChange(option.key, policy)" v-model="$store.getters.getContextSelection(option.key).id" :value="policy.id">
                  <div class="c-context-select__selection-list-text">
                    <div class="c-context-select__selection-list-text-title">
                      <abbr :title="policy.title">{{ policy.title }}</abbr>
                    </div>
                    <div class="c-context-select__selection-icons">
                      <favorite-selection :policy="policy" v-if="policy.id !== null" :favorite-items="favoriteItems" @add-favorite="addFavorite" @remove-favorite="removeFavorite" />
                      <font-awesome-icon v-if="index < options.length - 1 && policy.id !== null" :icon="['fal', 'chevron-right']" class="c-context-select__selection-list-text-icon-angle"/>
                    </div>
                  </div>
                </label>
              </li>
            </ul>
          </template>
        </content-loader>
      </div>

      <base-pagination class="p-2" :pagination="option.pagination" @change="onPageNumberSelect(option.key, {...option.pagination, pageNumber: $event })" :show-page-size-select="false" :is-compact="true"/>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'

import businessService from 'Services/BusinessService'
import userService from 'Services/UserService'

import { favorite } from '@/views/Context/Mixins'

import { Filter } from '@/classes/filters'
import PaginationRequest from '@/classes/PaginationRequest'
import { BusinessPolicy, MerchantPolicy } from '@/classes/models'

import FavoriteSelection from '@/views/Context/Components/FavoriteSelection'

import ContentLoader from '@/components/ContentLoader/ContentLoader.vue'

const state = Vue.observable({ isLoading: true, options: [], activeOption: null })

const defaultPagination = {
  pageNumber: 1,
  pageSize: 10,
  totalCount: 0
}

export default {
  name: 'DecisionTreeSelector',

  components: { FavoriteSelection, ContentLoader },

  mixins: [ favorite ],

  props: {
    liveOnly: {
      type: Boolean,
      default: false
    }
  },

  created () {
    this.$store.dispatch('setSelection', { key: 'business' })
    this.$store.dispatch('removeSelection', { key: 'merchant' })

    this.options = [{
      key: 'business',
      name: this.$i18n.t('label.businesses'),
      isLoading: false,
      canShowEmptyOption: false,
      policies: null,
      pagination: { ...defaultPagination }
    }, {
      key: 'merchant',
      name: this.$i18n.t('label.merchants'),
      isLoading: false,
      canShowEmptyOption: false,
      policies: null,
      pagination: { ...defaultPagination }
    }]

    this.activeOption = { key: 'business' }
  },

  mounted () {
    this.initialize().then(({ autoApply = false }) => {
      if (autoApply) {
        this.onSubmit(this.preferredRoute)
      }
    })
},

  computed: {
    isLoading: {
      get () {
        return state.isLoading
      },
      set (value) {
        state.isLoading = value
      }
    },

    activeOption: {
      get () {
        return state.activeOption
      },
        /**
       * Set active step, So we know which selection to show on smaller screens
       *
       * @param {string} key
       */
      set ({ key }) {
        if (state.options.findIndex(item => item.key === key) >= 0) {
          state.activeOption = key
        }
      }
    },

    options: {
      get () {
        return state.options
      },
      set (value) {
        state.options = value
      }
    },

    ...mapState({
      minimumSelections: state => state.context.minimumSelections
    })
  },

  methods: {
    optionPolicies (option, index) {
      const emptyOption = { title: this.$i18n.t('sentence.continueWithout{label}', { label: this.$i18n.t(`label.${option.key}`) }), id: null }

      return index >= this.minimumSelections.length && option.canShowEmptyOption && option.policies !== null ? [emptyOption, ...option.policies] : (option.policies || [])
    },

    /**
     * Merge option object by key
     *
     * @param {string} key
     * @param {object} option
     */
    updateOption ({ key, option }) {
      return new Promise((resolve) => {
        const { options } = this

        if (this.options.findIndex(item => item.key === key) >= 0) {
          Object.assign(options[options.findIndex(item => item.key === key)], option)

          this.options = options
          resolve()
        }
      })
    },

    /**
     * If the user have roles on cashflows (root) level, fetch the business. End of function.
     * If the user have no roles on cashflows (root) level, there will be business inside the policy structure, show these business.
     * If there is one business policy, select it and continue to merchant selection.
     *
     * If the user have roles on business level, fetch the merchants. End of function.
     * If the user have no roles on business level, there will be merchant inside the selected business policy, show these business.
     * If there is one merchant policy, select it and apply.
     */
    initialize () {
      const policy = this.$store.state.authorization.policy

      return new Promise((resolve) => {
        this.isLoading = true

        if (policy.roles.length > 0 || typeof policy.partnerPolicy !== 'undefined') {
          this.isLoading = false

          // Fetch businesses directly from server - only possible because of roles on cashflows level.
          this.getBusinessOptions({ filterContext: new PaginationRequest(defaultPagination.pageNumber, defaultPagination.pageSize, [new Filter(null, 'liveOnly', this.liveOnly)]) })

          if (typeof policy.partnerPolicy !== 'undefined') {
            this.$store.dispatch('setSelection', { key: 'partner', policy: { id: policy.partnerPolicy.partnerId, title: policy.partnerPolicy.partnerName } })
          }

          resolve({ autoApply: false })
        } else {
          // Use the businesses in the already loaded policy.
          this.updateOption({ key: 'business', option: { policies: policy.policies } })

          if (policy.policies.length === 1) {
            const { businessId, businessName, policies, roles } = policy.policies[0]

            this.$store.dispatch('setSelection', { key: 'business', policy: { id: businessId, title: businessName } })
            this.activeOption = { key: 'merchant' }

            if (roles.length) {
              this.isLoading = false

              // Fetch the merchants directly from server - only possible because of roles on business level.
              this.getMerchantOptions({ context: this.$store.getters.getContext(), filterContext: new PaginationRequest(defaultPagination.pageNumber, defaultPagination.pageSize, [new Filter(null, 'liveOnly', this.liveOnly)]) })
              resolve({ autoApply: false })
            } else {
              this.$store.dispatch('setSelection', { key: 'merchant', policy: null })
              // Use the merchants in the already loaded policy.
              this.updateOption({ key: 'merchant', option: { policies: policies } })

              if (policies.length === 1) {
                const { merchantId, merchantName } = policies[0]

                this.$store.dispatch('setSelection', { key: 'merchant', policy: { id: merchantId, title: merchantName } }).then(() => {
                  this.isLoading = false
                  resolve({ autoApply: true })
                })
              } else {
                this.isLoading = false
                resolve({ autoApply: false })
              }
            }
          } else {
            this.isLoading = false
            resolve({ autoApply: false })
          }
        }
      })
    },

     /**
     * Get business options.
     *
     * If there is one business policy, select it and continue to merchant selection.
     */
    getBusinessOptions ({ filterContext }) {
      return new Promise((resolve) => {
        this.$store.dispatch('setSelection', { key: 'business', policy: null })
        this.updateOption({ key: 'business', option: { isLoading: true } })

        const { request } = userService.getBusinesses(filterContext)

        request.then(response => {
          const policies = response.data.map(business => {
            const { businessId, businessName } = business

            return new BusinessPolicy({ businessId, businessName })
          })

          const { pagination } = state.options.find(item => item.key === 'business')

          this.updateOption({ key: 'business', option: { policies: policies, pagination: { ...pagination, totalCount: response.totalCount } } })

          if (policies.length === 1) {
            const { id, title } = policies[0]

            this.$store.dispatch('setSelection', { key: 'business', policy: { id, title } })
            this.getMerchantOptions({ context: this.$store.getters.getContext(), filterContext })
          } else if (policies.length === 0 && state.minimumSelections.length < 1) {
            this.$store.dispatch('setSelection', { key: 'business', policy: { id: null } })
          }
        }).finally(() => {
          this.updateOption({ key: 'business', option: { isLoading: false, canShowEmptyOption: true } }).then(resolve)
        })
      })
    },

    /**
     * Get merchant options.
     *
     * If there is one merchant policy, select it.
     */
    getMerchantOptions ({ context, filterContext }) {
      return new Promise((resolve) => {
        this.$store.dispatch('setSelection', { key: 'merchant', policy: null })
        this.updateOption({ key: 'merchant', option: { isLoading: true } })

        businessService.getMerchants(context, filterContext).then(response => {
          const policies = response.data.map(merchant => {
            const { merchantId, merchantName } = merchant

            return new MerchantPolicy({ businessId: context.business.id, merchantId, merchantName })
          })

          const { pagination } = state.options.find(item => item.key === 'merchant')

          this.updateOption({ key: 'merchant', option: { policies: policies, pagination: { ...pagination, totalCount: response.totalCount } } })

          if (policies.length === 1) {
            const { id, title } = policies[0]

            this.$store.dispatch('setSelection', { key: 'merchant', policy: { id, title } })
          } else if (policies.length === 0 && state.minimumSelections.length < 2) {
            this.$store.dispatch('setSelection', { key: 'merchant', policy: { id: null } })
          }
        }).finally(() => {
          this.updateOption({ key: 'merchant', option: { isLoading: false, canShowEmptyOption: true } }).then(resolve)
        })
      })
    },

    onPageNumberSelect (key, { pageNumber, pageSize }) {
      const { pagination } = this.options.find(item => item.key === key)
      const request = new PaginationRequest(pageNumber, pageSize, [new Filter(null, 'liveOnly', this.liveOnly)])

      this.updateOption({ key, option: { pagination: { ...pagination, pageNumber } } })

      if (key === 'business') {
        this.getBusinessOptions({ filterContext: request })
      } else {
        this.getMerchantOptions({ context: this.$store.getters.getContext(), filterContext: request })
      }
    },

    /**
     * On select change
     */
    onSelectChange: function (key, { id, title }) {
      const policy = this.$store.state.authorization.policy

      // stop loading for every option
      this.options.forEach(option => this.updateOption({ key: option.key, option: { isLoading: false } }))

      // set selected option
      this.$store.dispatch('setSelection', { key, policy: { id, title } })

      if (key === 'business') {
        this.updateOption({ key: 'merchant', option: { canShowEmptyOption: false, policies: null } })

        if (id === null) {
          this.$store.dispatch('removeSelection', { key: 'merchant' })

          this.activeOption = { key: 'business' }
        } else {
          this.$store.dispatch('setSelection', { key: 'merchant', policy: null })

          this.activeOption = { key: 'merchant' }

          if (policy.hasAnyBusinessRole(this.$store.getters.getContext())) {
            this.getMerchantOptions({ context: this.$store.getters.getContext(), filterContext: new PaginationRequest(defaultPagination.pageNumber, defaultPagination.pageSize, [new Filter(null, 'liveOnly', this.liveOnly)]) })
          } else {
            const { policies } = policy.businessPolicy(this.$store.getters.getContext())

            this.updateOption({ key: 'merchant', option: { policies: policies } })

            if (policies.length === 1) {
              const { id: merchantId, title } = policies[0]

              this.$store.dispatch('setSelection', { key: 'merchant', policy: { id: merchantId, title } })
            }
          }
        }
      } else if (key === 'merchant' && id === null) {
        this.activeOption = { key: 'merchant' }
      }
    },

    onPreviousOption (index) {
      const option = this.options[index]
      const previousOption = this.options[index - 1]

      this.$store.dispatch('setSelection', { key: option.key, policy: null })
      this.$store.dispatch('setSelection', { key: previousOption.key, policy: null })
      this.activeOption = { key: previousOption.key }

      if (previousOption.policies.length === 1 && index > 1) {
        setTimeout(() => { this.onPreviousOption(index - 1) }, 550)
      }
    }
  }
}
</script>
